/**
 * RTR Console — the standalone sub-tab under Investigate. Lists every
 * command in the tenant's queue with state pill, kind, device, reason,
 * and a per-row drawer with stdout/stderr when state >= acked.
 */
import { useMemo, useState } from "react";
import { formatDistanceToNow, format } from "date-fns";
import { Filter, Terminal, X } from "lucide-react";

import { Panel } from "@/components/surface/Panel";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";

import { RtrStatePill } from "./RtrStatePill";
import { useCancelRtrCommand, useRtrCommands } from "@/hooks/security/useRtr";
import type { RtrState, SensorCommand } from "@/types/edr-rtr";

const STATES: Array<RtrState | "all"> = [
  "all",
  "queued",
  "delivered",
  "acked",
  "completed",
  "failed",
  "expired",
  "cancelled",
];

const TERMINAL_OR_RUNNING: RtrState[] = [
  "acked",
  "completed",
  "failed",
  "expired",
];

export function RtrConsoleTab() {
  const [state, setState] = useState<(typeof STATES)[number]>("all");
  const [selected, setSelected] = useState<SensorCommand | null>(null);

  const q = useRtrCommands({
    state: state === "all" ? undefined : (state as RtrState),
    limit: 200,
  });
  const commands = q.data ?? [];
  const cancel = useCancelRtrCommand();

  const rows = useMemo(
    () => [...commands].sort((a, b) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    ),
    [commands],
  );

  return (
    <>
      <Panel
        title={`RTR Console · ${rows.length} command${rows.length === 1 ? "" : "s"}`}
        loading={q.isLoading}
        isError={q.isError}
        error={q.error}
        onRetry={() => q.refetch()}
        empty={!q.isLoading && !q.isError && rows.length === 0}
        emptyTitle="No commands yet"
        emptyDescription="Fire an RTR action from a Triage alert's Respond tab to see it here."
        skeleton="table"
      >
        <div className="mb-3 flex items-center gap-2">
          <Filter className="h-3.5 w-3.5 text-muted-foreground" />
          <Select value={state} onValueChange={(v) => setState(v as RtrState | "all")}>
            <SelectTrigger className="h-8 w-[140px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATES.map((s) => (
                <SelectItem key={s} value={s} className="text-xs">
                  {s === "all" ? "All states" : s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="-mx-4 -my-4 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[120px] text-[10px] uppercase tracking-wider">When</TableHead>
                <TableHead className="w-[110px] text-[10px] uppercase tracking-wider">State</TableHead>
                <TableHead className="w-[140px] text-[10px] uppercase tracking-wider">Kind</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Device</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Reason</TableHead>
                <TableHead className="w-[80px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((c) => (
                <TableRow
                  key={c.id}
                  className="cursor-pointer hover:bg-muted/40"
                  onClick={() => setSelected(c)}
                >
                  <TableCell
                    className="text-xs text-muted-foreground tabular-nums"
                    title={format(new Date(c.createdAt), "PPpp")}
                  >
                    {formatDistanceToNow(new Date(c.createdAt), { addSuffix: true })}
                  </TableCell>
                  <TableCell>
                    <RtrStatePill state={c.state} />
                  </TableCell>
                  <TableCell className="text-xs font-mono text-foreground">{c.kind}</TableCell>
                  <TableCell className="text-xs font-mono text-muted-foreground truncate max-w-[200px]">
                    {c.deviceId}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground truncate max-w-[280px]">
                    {c.reason || "—"}
                  </TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    {(c.state === "queued" || c.state === "delivered") && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-[11px]"
                        disabled={cancel.isPending}
                        onClick={() => cancel.mutate(c.id)}
                      >
                        <X className="h-3 w-3 mr-1" /> Cancel
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Panel>

      <RtrCommandDrawer
        command={selected}
        onOpenChange={(o) => !o && setSelected(null)}
      />
    </>
  );
}

function RtrCommandDrawer({
  command,
  onOpenChange,
}: {
  command: SensorCommand | null;
  onOpenChange: (open: boolean) => void;
}) {
  const open = command !== null;
  const showOutput = command && TERMINAL_OR_RUNNING.includes(command.state);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <span className="font-mono text-base">{command?.kind ?? ""}</span>
            {command && <RtrStatePill state={command.state} />}
          </SheetTitle>
          <SheetDescription>
            {command
              ? `Command ${command.id.slice(0, 12)}… on device ${command.deviceId}`
              : "Command details"}
          </SheetDescription>
        </SheetHeader>

        {command && (
          <div className="mt-4 space-y-4 text-xs">
            <Section label="Reason">
              <p className="text-foreground">{command.reason || "—"}</p>
            </Section>

            <Section label="Payload">
              <pre className="rounded-md bg-muted/60 px-3 py-2 font-mono text-[11px] overflow-x-auto">
                {JSON.stringify(command.payload, null, 2)}
              </pre>
            </Section>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Created">
                {format(new Date(command.createdAt), "PPpp")}
              </Field>
              {command.deliveredAt && (
                <Field label="Delivered">
                  {format(new Date(command.deliveredAt), "PPpp")}
                </Field>
              )}
              {command.ackedAt && (
                <Field label="Acked">{format(new Date(command.ackedAt), "PPpp")}</Field>
              )}
              {command.completedAt && (
                <Field label="Completed">
                  {format(new Date(command.completedAt), "PPpp")}
                </Field>
              )}
              {command.ttlMinutes != null && (
                <Field label="TTL">
                  <Badge variant="outline">{command.ttlMinutes} min</Badge>
                </Field>
              )}
              {command.exitCode != null && (
                <Field label="Exit code">
                  <Badge variant={command.exitCode === 0 ? "default" : "destructive"}>
                    {command.exitCode}
                  </Badge>
                </Field>
              )}
            </div>

            {showOutput && (
              <>
                <Section label="stdout">
                  <pre className="rounded-md bg-muted/60 px-3 py-2 font-mono text-[11px] overflow-x-auto max-h-64">
                    {command.stdout || "(empty)"}
                  </pre>
                </Section>
                {(command.stderr || command.error) && (
                  <Section label={command.error ? "Error" : "stderr"}>
                    <pre className="rounded-md bg-destructive/10 text-destructive px-3 py-2 font-mono text-[11px] overflow-x-auto max-h-64">
                      {command.error || command.stderr}
                    </pre>
                  </Section>
                )}
              </>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        {label}
      </div>
      {children}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        {label}
      </div>
      <div className="mt-0.5 text-xs text-foreground tabular-nums">{children}</div>
    </div>
  );
}
